import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/layouts/AdminLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Pencil, Power, Loader2, ExternalLink, Eye, EyeOff, ArrowRight, Wand2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";

const SLUG_RE = /^[a-z0-9-]{3,32}$/;
const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

const emptyTenant = {
  slug: "",
  name: "",
  tagline: "",
  owner_email: "",
  owner_password: "",
  owner_full_name: "",
  theme_color: "#7C3AED",
  accent_color: "#F59E0B",
  logo_url: "",
  plan: "starter",
};

export default function AdminDashboard() {
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editTenant, setEditTenant] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [t, u, s] = await Promise.all([
        api.get("/super-admin/tenants"),
        api.get("/super-admin/users"),
        api.get("/super-admin/stats"),
      ]);
      setTenants(t.data);
      setUsers(u.data);
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter(
      (t) => t.slug.includes(q) || t.name.toLowerCase().includes(q) || (t.owner_email || "").toLowerCase().includes(q),
    );
  }, [tenants, search]);

  return (
    <AdminLayout>
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">Super admin</p>
          <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl tracking-tight">Control room</h1>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="admin-stats-row">
          <StatTile label="Tenants" value={stats.tenants_total} subline={`${stats.tenants_active} active · ${stats.tenants_suspended} suspended`} testid="stat-tenants" />
          <StatTile label="Studio admins" value={stats.tenant_admins} testid="stat-tenant-admins" />
          <StatTile label="Subscribers" value={stats.subscribers} testid="stat-subscribers" />
          <StatTile label="All users" value={stats.users_total} testid="stat-users-total" />
        </div>
      )}

      <Tabs defaultValue="tenants" className="w-full">
        <TabsList className="bg-[#121212] border border-white/10 rounded-none p-1 h-auto">
          <TabsTrigger value="tenants" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black uppercase text-[11px] tracking-[0.25em] px-4 py-2" data-testid="admin-tab-tenants">Tenants</TabsTrigger>
          <TabsTrigger value="users" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black uppercase text-[11px] tracking-[0.25em] px-4 py-2" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="stats" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black uppercase text-[11px] tracking-[0.25em] px-4 py-2" data-testid="admin-tab-stats">Stats</TabsTrigger>
        </TabsList>

        <TabsContent value="tenants" className="mt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <div className="relative w-full md:w-80">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                placeholder="Search by slug, name, or owner"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-transparent border-white/15 rounded-none h-10 focus-visible:border-white"
                data-testid="admin-tenants-search"
              />
            </div>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <div className="flex items-center gap-2">
                <Link
                  to="/admin/onboard"
                  className="rounded-none bg-amber-400 text-black hover:bg-amber-300 uppercase text-[11px] tracking-[0.25em] h-10 inline-flex items-center px-4"
                  data-testid="admin-onboard-button"
                >
                  <Wand2 className="w-3.5 h-3.5 mr-1.5" /> Launch wizard
                </Link>
                <DialogTrigger asChild>
                  <Button variant="ghost" className="rounded-none border border-white/15 text-white/70 hover:text-white uppercase text-[11px] tracking-[0.25em] h-10" data-testid="admin-create-tenant-button">
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Quick create
                  </Button>
                </DialogTrigger>
              </div>
              <CreateTenantDialog
                onSuccess={() => {
                  setCreateOpen(false);
                  refresh();
                }}
              />
            </Dialog>
          </div>

          <div className="border border-white/10 bg-[#121212]">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-[10px] uppercase tracking-[0.25em] text-white/40">Studio</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.25em] text-white/40">Slug</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.25em] text-white/40">Owner</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.25em] text-white/40">Plan</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.25em] text-white/40">Status</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.25em] text-white/40">White label</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.25em] text-white/40">Content</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.25em] text-white/40">Subs</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.25em] text-white/40">30d revenue</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.25em] text-white/40 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="admin-tenants-table">
                {loading ? (
                  <TableRow><TableCell colSpan={10} className="text-center text-white/40 py-12 text-xs uppercase tracking-widest">Loading…</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="text-center text-white/40 py-12 text-xs uppercase tracking-widest">No tenants yet</TableCell></TableRow>
                ) : (
                  filtered.map((t) => (
                    <TableRow key={t.id} className="border-white/10 hover:bg-white/5" data-testid={`admin-tenant-row-${t.slug}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-sm" style={{ backgroundColor: t.theme_color }} />
                          <div>
                            <div className="font-medium text-sm">{t.name}</div>
                            <div className="text-[11px] text-white/40">{t.tagline}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><span className="font-mono text-xs text-white/70">{t.slug}</span></TableCell>
                      <TableCell><span className="text-xs text-white/70">{t.owner_email}</span></TableCell>
                      <TableCell><Badge variant="outline" className="rounded-none uppercase text-[10px] tracking-[0.2em]">{t.plan}</Badge></TableCell>
                      <TableCell>
                        <Badge className={`rounded-none uppercase text-[10px] tracking-[0.2em] ${t.status === "active" ? "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/20" : "bg-red-500/15 text-red-300 hover:bg-red-500/20"}`}>
                          {t.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!["pro", "enterprise"].includes(t.plan)}
                          title={!["pro", "enterprise"].includes(t.plan) ? "Available on Pro+" : ""}
                          className={`rounded-none uppercase text-[10px] tracking-[0.2em] h-7 px-2 ${t.white_label_enabled ? "bg-amber-500/15 text-amber-300 hover:bg-amber-500/20" : "bg-white/5 text-white/50 hover:text-white"} disabled:opacity-40 disabled:cursor-not-allowed`}
                          onClick={async () => {
                            try {
                              await api.patch(`/super-admin/tenants/${t.id}`, { white_label_enabled: !t.white_label_enabled });
                              toast.success(`White label ${!t.white_label_enabled ? "enabled" : "disabled"}`);
                              refresh();
                            } catch (e) {
                              toast.error(formatApiError(e.response?.data?.detail) || e.message);
                            }
                          }}
                          data-testid={`wl-toggle-${t.slug}`}
                        >
                          {t.white_label_enabled ? <><EyeOff className="w-3 h-3 mr-1" /> Hidden</> : <><Eye className="w-3 h-3 mr-1" /> Powered by</>}
                        </Button>
                      </TableCell>
                      <TableCell className="text-sm text-white/70">{t.content_count ?? 0}</TableCell>
                      <TableCell className="text-sm text-white/70">{t.subscriber_count}</TableCell>
                      <TableCell className="text-sm font-mono text-white/70">
                        {(t.revenue_30d_by_currency || []).length === 0 ? (
                          <span className="text-white/30">—</span>
                        ) : (
                          (t.revenue_30d_by_currency || []).map((r) => (
                            <div key={r.currency}>{formatMoney(r.total, r.currency)}</div>
                          ))
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/admin/tenants/${t.id}`}
                            className="text-white/55 hover:text-amber-300 inline-flex items-center text-[10px] uppercase tracking-[0.25em] gap-1"
                            title="Open detail"
                            data-testid={`admin-tenant-detail-${t.slug}`}
                          >
                            Open <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                          <Link
                            to={`/s/${t.slug}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-white/50 hover:text-white"
                            title="Open storefront"
                            data-testid={`admin-tenant-view-${t.slug}`}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Link>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-white/50 hover:text-white" onClick={() => setEditTenant(t)} data-testid={`admin-tenant-edit-${t.slug}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            className={`h-8 w-8 ${t.status === "active" ? "text-red-300 hover:text-red-200" : "text-emerald-300 hover:text-emerald-200"}`}
                            onClick={async () => {
                              try {
                                if (t.status === "active") {
                                  await api.delete(`/super-admin/tenants/${t.id}`);
                                  toast.success(`${t.name} suspended`);
                                } else {
                                  await api.patch(`/super-admin/tenants/${t.id}`, { status: "active" });
                                  toast.success(`${t.name} reactivated`);
                                }
                                refresh();
                              } catch (e) {
                                toast.error(formatApiError(e.response?.data?.detail) || e.message);
                              }
                            }}
                            data-testid={`admin-tenant-toggle-${t.slug}`}
                          >
                            <Power className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <Dialog open={!!editTenant} onOpenChange={(o) => !o && setEditTenant(null)}>
            {editTenant && (
              <EditTenantDialog
                tenant={editTenant}
                onSuccess={() => {
                  setEditTenant(null);
                  refresh();
                }}
              />
            )}
          </Dialog>
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <div className="border border-white/10 bg-[#121212]">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-[10px] uppercase tracking-[0.25em] text-white/40">Email</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.25em] text-white/40">Name</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.25em] text-white/40">Role</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.25em] text-white/40">Tenant</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="admin-users-table">
                {users.map((u) => (
                  <TableRow key={u.id} className="border-white/10 hover:bg-white/5">
                    <TableCell className="text-sm">{u.email}</TableCell>
                    <TableCell className="text-sm text-white/70">{u.full_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="rounded-none uppercase text-[10px] tracking-[0.2em]">
                        {u.role.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-white/60">{u.tenant_slug || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="stats" className="mt-6">
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="admin-stats-grid">
              <StatTile label="Tenants total" value={stats.tenants_total} testid="stats-tenants-total" />
              <StatTile label="Tenants active" value={stats.tenants_active} testid="stats-tenants-active" />
              <StatTile label="Tenants suspended" value={stats.tenants_suspended} testid="stats-tenants-suspended" />
              <StatTile label="Super admins" value={stats.super_admins} testid="stats-super-admins" />
              <StatTile label="Studio admins" value={stats.tenant_admins} testid="stats-studio-admins" />
              <StatTile label="Subscribers" value={stats.subscribers} testid="stats-subscribers-card" />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}

function StatTile({ label, value, subline, testid }) {
  return (
    <div className="bg-[#121212] border border-white/10 p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">{label}</div>
      <div className="mt-3 font-display font-bold text-3xl tracking-tight">{value}</div>
      {subline && <div className="mt-1 text-[11px] text-white/40">{subline}</div>}
    </div>
  );
}

function CreateTenantDialog({ onSuccess }) {
  const [form, setForm] = useState(emptyTenant);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!SLUG_RE.test(form.slug)) e.slug = "Slug must match ^[a-z0-9-]{3,32}$";
    if (!form.name.trim()) e.name = "Required";
    if (!form.owner_email.trim()) e.owner_email = "Required";
    if (form.owner_password.length < 6) e.owner_password = "Min 6 chars";
    if (!form.owner_full_name.trim()) e.owner_full_name = "Required";
    if (!HEX_RE.test(form.theme_color)) e.theme_color = "Hex color (#RRGGBB)";
    if (!HEX_RE.test(form.accent_color)) e.accent_color = "Hex color (#RRGGBB)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/super-admin/tenants", form);
      toast.success(`Tenant ${form.name} created`);
      onSuccess();
    } catch (err) {
      setServerError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="bg-[#0d0d0d] border-white/10 rounded-none text-white max-w-2xl" data-testid="create-tenant-dialog">
      <DialogHeader>
        <DialogTitle className="font-display font-bold text-2xl tracking-tight">Create tenant</DialogTitle>
        <DialogDescription className="text-white/50">Provision a new studio with its admin owner.</DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Slug" hint="lowercase, 3–32 chars" error={errors.slug}>
          <Input value={form.slug} onChange={(e) => set("slug", e.target.value.toLowerCase())} placeholder="studio-aurora" className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="create-tenant-slug" />
        </Field>
        <Field label="Display name" error={errors.name}>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Studio Aurora" className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="create-tenant-name" />
        </Field>
        <Field label="Tagline" className="md:col-span-2">
          <Input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} placeholder="Indie cinema, reimagined." className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="create-tenant-tagline" />
        </Field>
        <Field label="Logo URL" className="md:col-span-2">
          <Input value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://…/logo.png" className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="create-tenant-logo-url" />
        </Field>
        <ColorField label="Theme color" value={form.theme_color} onChange={(v) => set("theme_color", v)} error={errors.theme_color} testid="create-tenant-theme-color" />
        <ColorField label="Accent color" value={form.accent_color} onChange={(v) => set("accent_color", v)} error={errors.accent_color} testid="create-tenant-accent-color" />
        <Field label="Plan">
          <Select value={form.plan} onValueChange={(v) => set("plan", v)}>
            <SelectTrigger className="bg-transparent border-white/15 rounded-none" data-testid="create-tenant-plan">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 rounded-none text-white">
              <SelectItem value="starter">Starter</SelectItem>
              <SelectItem value="pro">Pro</SelectItem>
              <SelectItem value="enterprise">Enterprise</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <div className="md:col-span-2 mt-2 border-t border-white/10 pt-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Studio admin</div>
        </div>
        <Field label="Owner full name" error={errors.owner_full_name}>
          <Input value={form.owner_full_name} onChange={(e) => set("owner_full_name", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="create-tenant-owner-name" />
        </Field>
        <Field label="Owner email" error={errors.owner_email}>
          <Input value={form.owner_email} onChange={(e) => set("owner_email", e.target.value)} type="email" className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="create-tenant-owner-email" />
        </Field>
        <Field label="Owner password" error={errors.owner_password} className="md:col-span-2">
          <Input value={form.owner_password} onChange={(e) => set("owner_password", e.target.value)} type="text" className="bg-transparent border-white/15 rounded-none focus-visible:border-white font-mono" data-testid="create-tenant-owner-password" />
        </Field>
        {serverError && (
          <div className="md:col-span-2 text-sm text-red-400 border border-red-500/30 bg-red-500/5 p-3" data-testid="create-tenant-error">
            {serverError}
          </div>
        )}
        <DialogFooter className="md:col-span-2 mt-2">
          <Button type="submit" disabled={submitting} className="rounded-none bg-white text-black hover:bg-amber-300 uppercase text-[11px] tracking-[0.25em]" data-testid="create-tenant-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create tenant"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function EditTenantDialog({ tenant, onSuccess }) {
  const [form, setForm] = useState({
    name: tenant.name,
    tagline: tenant.tagline,
    logo_url: tenant.logo_url || "",
    theme_color: tenant.theme_color,
    accent_color: tenant.accent_color,
    plan: tenant.plan,
    status: tenant.status,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!HEX_RE.test(form.theme_color) || !HEX_RE.test(form.accent_color)) {
      setError("Colors must be valid hex (#RRGGBB)");
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/super-admin/tenants/${tenant.id}`, form);
      toast.success(`${form.name} updated`);
      onSuccess();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="bg-[#0d0d0d] border-white/10 rounded-none text-white max-w-2xl" data-testid="edit-tenant-dialog">
      <DialogHeader>
        <DialogTitle className="font-display font-bold text-2xl tracking-tight">Edit {tenant.name}</DialogTitle>
        <DialogDescription className="text-white/50">Slug <span className="font-mono text-white/70">{tenant.slug}</span> cannot be changed.</DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Display name">
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="edit-tenant-name" />
        </Field>
        <Field label="Tagline">
          <Input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="edit-tenant-tagline" />
        </Field>
        <Field label="Logo URL" className="md:col-span-2">
          <Input value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="edit-tenant-logo" />
        </Field>
        <ColorField label="Theme color" value={form.theme_color} onChange={(v) => set("theme_color", v)} testid="edit-tenant-theme" />
        <ColorField label="Accent color" value={form.accent_color} onChange={(v) => set("accent_color", v)} testid="edit-tenant-accent" />
        <Field label="Plan">
          <Select value={form.plan} onValueChange={(v) => set("plan", v)}>
            <SelectTrigger className="bg-transparent border-white/15 rounded-none" data-testid="edit-tenant-plan"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 rounded-none text-white">
              <SelectItem value="starter">Starter</SelectItem>
              <SelectItem value="pro">Pro</SelectItem>
              <SelectItem value="enterprise">Enterprise</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Status">
          <Select value={form.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger className="bg-transparent border-white/15 rounded-none" data-testid="edit-tenant-status"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 rounded-none text-white">
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        {error && <div className="md:col-span-2 text-sm text-red-400 border border-red-500/30 bg-red-500/5 p-3">{error}</div>}
        <DialogFooter className="md:col-span-2">
          <Button type="submit" disabled={submitting} className="rounded-none bg-white text-black hover:bg-amber-300 uppercase text-[11px] tracking-[0.25em]" data-testid="edit-tenant-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function Field({ label, hint, error, className = "", children }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50">{label}</Label>
      {children}
      {hint && !error && <div className="text-[10px] text-white/30">{hint}</div>}
      {error && <div className="text-[11px] text-red-400">{error}</div>}
    </div>
  );
}

function ColorField({ label, value, onChange, error, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50">{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={HEX_RE.test(value) ? value : "#000000"}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="h-10 w-10 bg-transparent border border-white/15 cursor-pointer p-0"
          data-testid={`${testid}-picker`}
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="bg-transparent border-white/15 rounded-none focus-visible:border-white font-mono uppercase"
          data-testid={`${testid}-input`}
        />
      </div>
      {error && <div className="text-[11px] text-red-400">{error}</div>}
    </div>
  );
}
